import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { PROJECT_SLUG_TO_ID } from "../../Constants/projectMapping";
import axios from "axios";
import { GrFormNext } from "react-icons/gr";

const MithaHarithamProject = ({ projectData: propProjectData }) => {
  const { id } = useParams();

  // Resolve numeric ID
  const resolvedId = id && !isNaN(id) ? Number(id) : (PROJECT_SLUG_TO_ID[id] || null);

  const [activeTab, setActiveTab] = useState("overview");
  const [projectData, setProjectData] = useState(propProjectData || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [contentHeight, setContentHeight] = useState("500px");
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const galleryRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      updateContentHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update content height based on image height
  const updateContentHeight = () => {
    if (imageRef.current && !isMobile) {
      const imageHeight = imageRef.current.clientHeight;
      setContentHeight(`${imageHeight}px`);
    } else {
      setContentHeight(isMobile ? "auto" : "500px");
    }
  };

  // Update height when image loads
  useEffect(() => {
    updateContentHeight();
  }, [isMobile, projectData]);

  // Base API URLs
  const BASE_URL = "https://mithahomes.com/backend/api";
  const ImgURL = "https://mithahomes.com/backend";
  const PROJECT_URL = resolvedId ? `${BASE_URL}/mainprojectprocess/projectsgetapi/${resolvedId}` : null;

  useEffect(() => {
    const fetchProjectData = async () => {
      // If we already have data from props, don't fetch
      if (propProjectData) {
        setProjectData(propProjectData);
        setLoading(false);
        return;
      }

      if (!PROJECT_URL) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await axios.get(PROJECT_URL);

        if (response.data.success) {
          setProjectData(response.data.data);
          setError(null);
        } else {
          setError("Failed to load project data");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load project data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [PROJECT_URL, propProjectData]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Responsive Styles
  const containerStyle = {
    fontFamily: "'Roboto', 'Poppins', 'Segoe UI', Arial, sans-serif",
    margin: 0,
    padding: isMobile ? "10px" : "20px",
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)",
  };

  const projectContainerStyle = {
    maxWidth: "1300px",
    margin: "auto",
    backgroundColor: "rgb(244 246 249)",
    padding: isMobile ? "15px" : "30px",

    overflow: "hidden",
  };

  const headerStyle = {
    textAlign: "center",
    paddingBottom: isMobile ? "15px" : "25px",
  };

  const titleStyle = {
    fontSize: isMobile ? "22px" : "28px",
    color: "#000000ff",
    margin: "0 0 10px 0",
    fontWeight: "700",
    fontFamily: "'Roboto', sans-serif",
    letterSpacing: "0px",
  };

  const subtitleStyle = {
    fontSize: isMobile ? "16px" : "18px",
    color: "#555",
    margin: "8px 0",
    fontWeight: "400",
  };

  const highlightStyle = {
    color: "#000000ff",
    fontWeight: "600",
    display: "block",
  };

  const tabNavStyle = {
    display: "flex",
    marginBottom: isMobile ? "20px" : "30px",

    flexWrap: "wrap",
    overflowX: isMobile ? "auto" : "visible",
    paddingBottom: "5px",
    WebkitOverflowScrolling: "touch",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  };

  const tabButtonStyle = (isActive) => ({
    padding: isMobile ? "12px 15px" : "15px 25px",
    border: "none",
    backgroundColor: isActive ? "rgb(226 93 36)" : "#d3d4d5",
    cursor: "pointer",
    fontSize: isMobile ? "14px" : "16px",
    fontWeight: "600",
    color: isActive ? "#ffffffff" : "#666",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    transform: isActive ? "translateY(2px)" : "none",
    borderTop: isActive ? "3px solid #ffffffff" : "3px solid transparent",
    borderLeft: isActive ? "1px solid #f0f0f0" : "1px solid transparent",
    borderRight: isActive ? "1px solid #f0f0f0" : "1px solid transparent",
    flex: isMobile ? "0 0 auto" : "1",
    minWidth: isMobile ? "100px" : "120px",
    textAlign: "center",
    whiteSpace: "nowrap",
  });

  const mainContentStyle = {
    display: "flex",
    gap: isMobile ? "20px" : "40px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    flexDirection: isMobile ? "column" : "row",
  };

  const imageAreaStyle = {
    flex: "1",
    width: isMobile ? "100%" : "auto",
    minWidth: isMobile ? "100%" : "300px",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
  };

  const imageStyle = {
    width: "100%",
    height: "auto",
    objectFit: "inherit",
    display: "flex",
  };

  const contentAreaStyle = {
    flex: "1",
    width: isMobile ? "100%" : "auto",
    minWidth: isMobile ? "100%" : "300px",
    padding: isMobile ? "20px" : "25px",
    backgroundColor: "#fafafa",
    borderRadius: "12px",
    border: "1px solid #f0f0f0",
    height: isMobile ? "auto" : contentHeight,
    maxHeight: isMobile ? "none" : contentHeight,
    scrollbarWidth: "thin",
    scrollbarColor: "#e67e22 #f0f0f0",
  };

  const headingStyle = {
    fontSize: isMobile ? "15px" : "18px",
    color: "#2c3e50",
    marginBottom: "0px",
    fontFamily: "'Roboto', sans-serif",
    paddingBottom: "0px",
    borderBottom: "2px solid #f6f6f6ff",
    fontWeight: "600",
    position: "sticky",
    top: "0",
    backgroundColor: "#fafafa",
    zIndex: "1",
    paddingTop: "5px",
  };

  const paragraphStyle = {
    fontSize: isMobile ? "14px" : "16px",
    lineHeight: "1.4",
    color: "#34495e",
    marginBottom: "20px",
    fontFamily: "'Roboto', sans-serif",
  };

  // New Bullet List Styles
  const bulletListStyle = {
    listStyleType: "none",
    padding: "0",
    margin: "0",
  };

  const bulletListItemStyle = {
    padding: isMobile ? "12px 15px 12px 40px" : "0px 20px 10px 45px",
    borderRadius: "8px",
    fontSize: isMobile ? "14px" : "16px",
    color: "#2c3e50",
    position: "relative",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    fontFamily: "'Roboto', sans-serif",
  };

  const bulletIconStyle = {
    position: "absolute",
    left: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#1abc9c",
    fontSize: isMobile ? "16px" : "20px",
  };

  const scrollableListContainerStyle = {
    maxHeight: "400px",
    paddingRight: "10px",
  };

  const scrollableGridContainerStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "100%" : "300px"}, 1fr))`,
    gap: "15px",
    maxHeight: "400px",
    overflowY: "auto",
    paddingRight: "10px",
  };

  const featureCardStyle = {
    padding: isMobile ? "20px" : "25px",
    borderRadius: "10px",
    marginBottom: "20px",
  };

  const featureTitleStyle = {
    color: "#000000ff",
    fontSize: isMobile ? "16px" : "18px",
    fontWeight: "600",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    fontFamily: "'Roboto', sans-serif",
  };

  const statsContainerStyle = {
    display: "flex",
    gap: isMobile ? "10px" : "20px",
    marginTop: "25px",
    flexWrap: "wrap",
    justifyContent: isMobile ? "space-between" : "flex-start",
  };

  const statItemStyle = {
    textAlign: "center",
    flex: isMobile ? "0 0 calc(33% - 10px)" : "1",
    minWidth: isMobile ? "auto" : "100px",
  };

  const loadingStyle = {
    textAlign: "center",
    padding: isMobile ? "50px 20px" : "100px 20px",
    fontSize: isMobile ? "16px" : "18px",
    color: "#666",
  };

  const errorStyle = {
    textAlign: "center",
    padding: isMobile ? "50px 20px" : "100px 20px",
    color: "#e74c3c",
    fontSize: isMobile ? "16px" : "18px",
  };

  // Gallery styles
  const galleryContainerStyle = {
    display: "flex",
    overflowX: "auto",
    gap: "15px",
    padding: "15px 0",
    scrollbarWidth: "thin",
    scrollbarColor: "#e67e22 #f0f0f0",
    WebkitOverflowScrolling: "touch",
    maxWidth: "100%",
  };
  const highlightStyleq = {
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "27px",
    color: "rgb(33, 37, 41)",
    display: "block", // next line
    marginBottom: "4px",
  };
  const galleryImageStyle = {
    width: isMobile ? "250px" : "300px",
    height: isMobile ? "180px" : "200px",
    objectFit: "cover",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    flexShrink: 0,
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div
            style={{
              fontSize: isMobile ? "20px" : "24px",
              marginBottom: "20px",
            }}
          >
            Loading Project Details...
          </div>
          <div style={{ color: "#95a5a6" }}>
            Please wait while we fetch the project information
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <div
            style={{
              fontSize: isMobile ? "20px" : "24px",
              marginBottom: "20px",
            }}
          >
            ⚠️ Error Loading Project
          </div>
          <div>{error}</div>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#e67e22",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: isMobile ? "14px" : "16px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>Project not found</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
    
        
        .image-hover:hover {
          transform: scale(1.05);
          transition: transform 0.3s ease;
        }
        
        /* Custom scrollbar for content area */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f0f0f0;
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e67e22;
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d35400;
        }
        
        /* Custom scrollbar for lists */
        .list-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .list-scrollbar::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 4px;
        }
        
        .list-scrollbar::-webkit-scrollbar-thumb {
          background: #1abc9c;
          border-radius: 4px;
        }
        
        .list-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #16a085;
        }
        
        /* Hide scrollbar for tab navigation */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        /* Bullet point animation */
        @keyframes bulletAppear {
          0% { transform: scale(0); }
          100% { transform: scale(1); }
        }
        
        .bullet-item {
          animation: bulletAppear 0.3s ease-out;
        }
        
        /* Responsive media queries */
        @media (max-width: 768px) {
          .mobile-stack {
            flex-direction: column;
          }
          
          .mobile-full-width {
            width: 100%;
            flex: 0 0 100%;
          }
          
          .mobile-text-center {
            text-align: center;
          }
          
          .mobile-padding-sm {
            padding: 10px;
          }
          
          .mobile-margin-sm {
            margin: 5px;
          }
        }
        
        @media (max-width: 480px) {
          .mobile-hide {
            display: none;
          }
          
          .mobile-show {
            display: block;
          }
        }
        
        /* Ensure consistent heights */
        .equal-height-container {
          display: flex;
          flex-direction: row;
        }
        
        @media (max-width: 768px) {
          .equal-height-container {
            flex-direction: column;
          }
        }
      `}</style>

      <div style={projectContainerStyle}>
        <header style={headerStyle}>
          {/* {![89, 84].includes(Number(projectData?.id || resolvedId)) && (
            <h2 style={subtitleStyle}><strong style={{ color: "black" }}>{projectData.project_name}</strong></h2>
          )} */}
          <div
            style={{
              padding: "10px 0",
            }}
          >
            {/* <span style={highlightStyleq}>
              {projectData?.location || "Location not available"}
            </span> */}

            <h1 style={titleStyle}>
              {projectData?.project_caption || "Project caption not available"}
            </h1>
          </div>
        </header>

        {/* Tab Navigation */}
        <div style={tabNavStyle} className="hide-scrollbar">
          {["overview", "amenities", "location"].map((tab) => (
            <button
              key={tab}
              style={tabButtonStyle(activeTab === tab)}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Main Content - Equal Height Container */}
        <div style={mainContentStyle} className="equal-height-container">
          {/* Left Column - Image */}
          <div style={imageAreaStyle}>
            <img
              ref={imageRef}
              src={
                `${ImgURL}${projectData.home_image}` ||
                "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
              }
              alt={projectData.project_name}
              style={imageStyle}
              className="image-hover"
              onLoad={updateContentHeight}
            />
          </div>

          {/* Right Column - Content */}
          <div
            style={contentAreaStyle}
            ref={contentRef}
            className="custom-scrollbar"
          >
            <div style={{ animation: "fadeIn 0.5s ease" }}>
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div>
                  <h2 style={headingStyle}>Project Overview</h2>
                  <div style={featureCardStyle}>
                    <p style={paragraphStyle}>
                      <span style={highlightStyle}>
                        Discover {projectData.project_name} -                       {projectData.project_caption}
                      </span>{" "}
                    </p>
                    <p
                      style={paragraphStyle}
                      dangerouslySetInnerHTML={{
                        __html: projectData.project_overview,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Amenities Tab */}
              {activeTab === "amenities" &&
                projectData.amenities &&
                projectData.amenities.length > 0 && (
                  <div>
                    <h2 style={headingStyle}>World-Class Amenities</h2>
                    <p style={paragraphStyle}>
                      Experience luxury living with our comprehensive range of
                      amenities designed for your comfort and convenience.
                    </p>

                    <div
                      style={scrollableGridContainerStyle}
                      className="list-scrollbar"
                    >
                      <ul style={bulletListStyle}>
                        {projectData.amenities.map((amenity, index) => (
                          <li
                            key={index}
                            style={bulletListItemStyle}
                            className="bullet-item list-item"
                          >
                            <span style={bulletIconStyle}>•</span>
                            {amenity.amenities_name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

              {/* Location Tab */}
              {activeTab === "location" && (
                <div>
                  <h2 style={headingStyle}>Prime Location</h2>
                  <div style={featureCardStyle}>
                    <p style={paragraphStyle}>
                      Located in the prime area of {projectData.location},
                      offering excellent connectivity and accessibility.
                    </p>

                    {projectData.nearby_locations &&
                      projectData.nearby_locations.length > 0 && (
                        <>
                          <h3
                            style={{ ...featureTitleStyle, marginTop: "25px" }}
                          >
                            Nearby Locations
                          </h3>
                          <div
                            style={scrollableListContainerStyle}
                            className="list-scrollbar"
                          >
                            <ul style={bulletListStyle}>
                              {projectData.nearby_locations.map(
                                (location, index) => (
                                  <li
                                    key={index}
                                    style={bulletListItemStyle}
                                    className="bullet-item list-item"
                                  >
                                    <span style={bulletIconStyle}>
                                      <GrFormNext />
                                    </span>
                                    {location.nearby_location_name}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        </>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Gallery Section */}
        {(projectData.amenities_images || projectData.specification_images) && (
          <div
            style={{
              marginTop: "40px",
              paddingTop: "20px",
              borderTop: "2px solid #f0f0f0",
            }}
          >
            <h3
              style={{
                fontSize: isMobile ? "18px" : "22px",
                color: "#2c3e50",
                marginBottom: "20px",
                textAlign: "center",
                fontFamily: "'Roboto', sans-serif",
              }}
            >
              Project Gallery
            </h3>

            <div style={galleryContainerStyle} ref={galleryRef}>
              {projectData.amenities_images &&
                projectData.amenities_images.map((img, index) => (
                  <img
                    key={`amenity-${index}`}
                    src={`${ImgURL}${img.image}`}
                    alt={`Amenity ${index + 1}`}
                    style={galleryImageStyle}
                    className="image-hover"
                  />
                ))}

              {projectData.specification_images &&
                projectData.specification_images.map((img, index) => (
                  <img
                    key={`spec-${index}`}
                    src={`${ImgURL}${img.image}`}
                    alt={`Specification ${index + 1}`}
                    style={galleryImageStyle}
                    className="image-hover"
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MithaHarithamProject;
